<template>
  <NuxtLink v-if="to" :to="to" class="cy-btn" :class="classes">
    <span class="cy-btn-in"><slot /></span>
  </NuxtLink>
  <button v-else :type="type" class="cy-btn" :class="classes" :disabled="disabled">
    <span class="cy-btn-in"><slot /></span>
  </button>
</template>

<script setup>
// Console-key button. Notched, mono-tracked uppercase label, rim that
// ignites on hover. `primary` is the loud CTA (dark text on the tenant
// primary + hot glow); `ghost` is the quiet frame; `alarm` uses the
// accent for destructive/attention actions. Pass `to` to render a
// NuxtLink with identical chrome.
import { computed } from 'vue'

const props = defineProps({
  /* 'primary' | 'ghost' | 'alarm' */
  variant: { type: String, default: 'ghost' },
  /* 'md' (44px) | 'sm' (34px) */
  size: { type: String, default: 'md' },
  to: { type: [String, Object], default: null },
  type: { type: String, default: 'button' },
  disabled: { type: Boolean, default: false }
})

const classes = computed(() => [
  `cy-btn--${props.variant}`,
  `cy-btn--${props.size}`
])
</script>

<style scoped>
.cy-btn {
  --cy-n: var(--cy-notch-sm);
  position: relative;
  display: inline-flex;
  padding: 1px;                     /* reveals the rim layer */
  border: none;
  background: var(--cy-rim);
  clip-path: polygon(
    var(--cy-n) 0, 100% 0,
    100% calc(100% - var(--cy-n)), calc(100% - var(--cy-n)) 100%,
    0 100%, 0 var(--cy-n)
  );
  cursor: pointer;
  text-decoration: none;
  font-family: var(--cy-display);
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition: background var(--cy-t-fast) var(--cy-ease),
              filter var(--cy-t-fast) var(--cy-ease),
              transform var(--cy-t-fast) var(--cy-ease);
  flex-shrink: 0;
}
.cy-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.cy-btn-in {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  clip-path: polygon(
    var(--cy-n) 0, 100% 0,
    100% calc(100% - var(--cy-n)), calc(100% - var(--cy-n)) 100%,
    0 100%, 0 var(--cy-n)
  );
  background: var(--cy-module);
  color: var(--text-main);
  transition: background var(--cy-t-fast) var(--cy-ease),
              color var(--cy-t-fast) var(--cy-ease);
  white-space: nowrap;
}
.cy-btn--md .cy-btn-in { height: 44px; padding: 0 22px; font-size: 0.78rem; }
.cy-btn--sm .cy-btn-in { height: 32px; padding: 0 14px; font-size: 0.68rem; }

/* Ghost — quiet frame, ignites on hover. */
.cy-btn--ghost:hover:not(:disabled),
.cy-btn--ghost:focus-visible {
  background: var(--cy-rim-hot);
}
.cy-btn--ghost:hover:not(:disabled) .cy-btn-in {
  background: var(--cy-module-hi);
  color: var(--primary);
}

/* Primary — THE CTA. Dark text on the tenant primary; the only
   button allowed --cy-glow-hot (brief §3.4). */
.cy-btn--primary {
  background: var(--cy-rim-hot);
  filter: drop-shadow(0 0 14px var(--cy-glow-c));
}
.cy-btn--primary .cy-btn-in {
  background: var(--primary);
  color: var(--cy-ground);
}
.cy-btn--primary:hover:not(:disabled) {
  filter: drop-shadow(0 0 20px var(--cy-glow-hot-c));
  transform: translateY(-1px);
}
.cy-btn--primary:hover:not(:disabled) .cy-btn-in {
  background: color-mix(in srgb, var(--primary) 88%, white 12%);
}

/* Alarm — accent-framed. */
.cy-btn--alarm { background: var(--cy-alarm-dim); }
.cy-btn--alarm .cy-btn-in { color: var(--cy-alarm); }
.cy-btn--alarm:hover:not(:disabled) { background: var(--cy-alarm); }
.cy-btn--alarm:hover:not(:disabled) .cy-btn-in {
  background: color-mix(in srgb, var(--accent) 14%, var(--cy-module));
}

@media (prefers-reduced-motion: reduce) {
  .cy-btn { transition: none; }
  .cy-btn--primary:hover:not(:disabled) { transform: none; }
}
</style>
